import React, { useState, useRef, useEffect } from 'react';

interface ZoomableImageProps {
  src: string;
  alt: string;
  moveDown?: boolean;
  moveRight?: boolean;
  onLoad?: () => void;
}

const ZoomableImage: React.FC<ZoomableImageProps> = ({ src, alt, moveDown, moveRight, onLoad }) => {
  const [isZoomed, setIsZoomed] = useState(false);
  const [isPanning, setIsPanning] = useState(false);
  const [hasPanned, setHasPanned] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const imgRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const start = useRef({ x: 0, y: 0 });

  useEffect(() => {
    setIsZoomed(false);
  }, [src]);

  // Note: Now panStart/move/end handlers are on container div
  const panStart = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!isZoomed) return;

    setIsPanning(true);
    setHasPanned(false);
    start.current = { x: event.clientX, y: event.clientY };

    if (containerRef.current) {
      containerRef.current.setPointerCapture(event.pointerId);
      if (imgRef.current) imgRef.current.style.cursor = 'grabbing';
    }
  };

  const panMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!isZoomed || !isPanning) return;

    const deltaX = event.clientX - start.current.x;
    const deltaY = event.clientY - start.current.y;

    if (Math.abs(deltaX) > 2 || Math.abs(deltaY) > 2) {
      setHasPanned(true);
    }

    setOffset(prevOffset => ({
      x: prevOffset.x + deltaX / 2,
      y: prevOffset.y + deltaY / 2,
    }));

    start.current = { x: event.clientX, y: event.clientY };
  };

  const panEnd = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!isZoomed) return;
    setIsPanning(false);
    if (containerRef.current) {
      containerRef.current.releasePointerCapture(event.pointerId);
    }
    if (imgRef.current) imgRef.current.style.cursor = 'grab';
  };

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (hasPanned) return;

    const container = containerRef.current?.getBoundingClientRect();
    if (!container) return;

    const clickX = e.clientX - container.left;
    const clickY = e.clientY - container.top;

    const centerX = container.width / 2;
    const centerY = container.height / 2;

    const offsetX = (centerX - clickX) / 2;
    const offsetY = (centerY - clickY) / 2;

    setIsZoomed(prev => !prev);
    setPosition(isZoomed ? { x: 0, y: 0 } : { x: offsetX, y: offsetY });
    setOffset(isZoomed ? { x: 0, y: 0 } : offset);
  };

  const floorplanOffset = { x: moveRight ? '30px' : 0, y: moveDown ? '30px' : 0 };

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      ref={containerRef}
      className={`relative flex h-full w-full items-start justify-center overflow-hidden ${
        isZoomed ? 'touch-none' : ''
      }`}
      onClick={handleClick}
      onPointerDown={panStart}
      onPointerMove={panMove}
      onPointerUp={panEnd}
      onPointerCancel={panEnd}
    >
      <img
        ref={imgRef}
        onLoad={onLoad}
        src={src}
        alt={alt}
        className={`h-auto max-h-screen w-auto max-w-full cursor-grab ${
          isPanning ? '' : 'transition-transform duration-300 ease-in-out'
        }`}
        style={{
          objectFit: 'contain',
          transform: isZoomed
            ? `scale(2) translate(${position.x + offset.x}px, ${position.y + offset.y}px)`
            : `scale(1) translate(${floorplanOffset.x}, ${floorplanOffset.y})`,
          cursor: isZoomed ? 'grab' : 'pointer',
        }}
        draggable={false}
      />
    </div>
  );
};

export default ZoomableImage;
